import React, {
    createContext,
    useContext,
    useMemo,
    useState,
    ReactNode,
} from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Channel, ChannelGroup } from '../../../../shared/types';
import { GET_CHANNELS_AND_GROUPS } from '../../../../gql/channels';
import {
    getFilteredChannelsByGroups,
    getSortedChannels,
    getFilteredChannelsByType,
} from './components/channelFilterUtils';
import { ALL_GROUPS, FilterType, SortType, TypeFilter } from './types';
import {
    DEFAULT_FILTER_PARAM_VALUE,
    FILTER_PARAM_KEY,
    GROUP_PARAM_KEY,
    SORT_PARAM_KEY,
    TYPE_PARAM_KEY,
    getUrlSearchParams,
    updateUrlParams,
} from './components/FilterBar/urlParams';

type ChannelsAndGroupsData = {
    channels: Channel[];
    channelGroups: ChannelGroup[];
};

type FilterContextType = {
    filterType: FilterType;
    selectedGroupId: string;
    sortType: SortType;
    typeFilter: TypeFilter;
    hasGroups: boolean;
    channelGroupsWithChannels: ChannelGroup[];
    channels: Channel[];
    loading: boolean;

    setFilterType: (filterType: FilterType) => void;
    setSelectedGroupId: (groupId: string) => void;
    setSortType: (sortType: SortType) => void;
    setTypeFilter: (typeFilter: TypeFilter) => void;

    resetSortTypeToDefault: () => void;
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

type FilterProviderProps = {
    children: ReactNode;
};

const getInitialFilterType = (): FilterType => {
    const filterParam = (
        getUrlSearchParams().get(FILTER_PARAM_KEY) || DEFAULT_FILTER_PARAM_VALUE
    ).toLowerCase();

    if (filterParam === 'premium') {
        return FilterType.Premium;
    }

    return FilterType.All;
};

const getInitialSortType = (): SortType => {
    const sortParam = getUrlSearchParams().get(SORT_PARAM_KEY);

    if (sortParam === SortType.AZ || sortParam === SortType.ZA) {
        return sortParam;
    }

    return SortType.None;
};

const getInitialTypeFilter = (): TypeFilter => {
    const typeParam = getUrlSearchParams().get(TYPE_PARAM_KEY) as TypeFilter;

    if (typeParam === TypeFilter.Premium || typeParam === TypeFilter.Free) {
        return typeParam;
    }

    return TypeFilter.All;
};

const getFilterTypeParamValue = (filterType: FilterType): string => {
    switch (filterType) {
        case FilterType.Premium:
            return 'premium';
        default:
        case FilterType.All:
            return 'all';
    }
};

export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
    const [filterType, setFilterType] = useState<FilterType>(() =>
        getInitialFilterType()
    );
    const [selectedGroupId, setSelectedGroupId] = useState<string>(ALL_GROUPS);
    const [sortType, setSortType] = useState<SortType>(() =>
        getInitialSortType()
    );
    const [typeFilter, setTypeFilter] = useState<TypeFilter>(() =>
        getInitialTypeFilter()
    );

    const { loading, data } = useQuery<ChannelsAndGroupsData>(
        GET_CHANNELS_AND_GROUPS,
        {
            fetchPolicy: 'network-only',
            onCompleted: (data) => {
                const channelGroups = data.channelGroups || [];
                const channelGroupsWithChannels = channelGroups.filter(
                    (group) => (group.channelIds?.length ?? 0) > 0
                );
                const hasGroupsFromResponse =
                    channelGroupsWithChannels.length > 0;
                const groupParam = getUrlSearchParams().get(GROUP_PARAM_KEY);

                if (groupParam !== null) {
                    const normalizedGroupParam = groupParam.toLowerCase();

                    if (normalizedGroupParam === ALL_GROUPS) {
                        setSelectedGroupId(ALL_GROUPS);
                        return;
                    }

                    const matchingGroup = channelGroupsWithChannels.find(
                        (group) =>
                            group.name.toLowerCase() === normalizedGroupParam
                    );

                    if (matchingGroup) {
                        setSelectedGroupId(matchingGroup._id);
                        return;
                    }

                    setSelectedGroupId(ALL_GROUPS);
                    updateUrlParams(
                        { [GROUP_PARAM_KEY]: ALL_GROUPS },
                        hasGroupsFromResponse
                    );
                    return;
                }

                const selectedGroupExists = channelGroupsWithChannels.some(
                    (group) => group._id === selectedGroupId
                );

                if (!selectedGroupExists) {
                    setSelectedGroupId(ALL_GROUPS);
                    updateUrlParams(
                        { [GROUP_PARAM_KEY]: ALL_GROUPS },
                        hasGroupsFromResponse
                    );
                }
            },
        }
    );

    const allChannels = data?.channels || [];
    const channelGroups = data?.channelGroups || [];

    const channelGroupsWithChannels = useMemo(
        () =>
            channelGroups.filter(
                (group) => (group.channelIds?.length ?? 0) > 0
            ),
        [channelGroups]
    );

    const hasGroups = channelGroupsWithChannels.length > 0;

    const setFilterTypeAndSync = (nextFilterType: FilterType) => {
        setFilterType(nextFilterType);
        updateUrlParams(
            {
                [FILTER_PARAM_KEY]: getFilterTypeParamValue(nextFilterType),
            },
            hasGroups
        );
    };

    const setSelectedGroupIdAndSync = (groupId: string) => {
        setSelectedGroupId(groupId);

        const selectedGroup = channelGroupsWithChannels.find(
            (group) => group._id === groupId
        );
        const groupParamValue =
            groupId === ALL_GROUPS
                ? ALL_GROUPS
                : selectedGroup?.name || ALL_GROUPS;

        updateUrlParams(
            {
                [GROUP_PARAM_KEY]: groupParamValue,
            },
            hasGroups
        );
    };

    const setSortTypeAndSync = (nextSortType: SortType) => {
        setSortType(nextSortType);
        updateUrlParams(
            {
                [SORT_PARAM_KEY]: nextSortType,
            },
            hasGroups
        );
    };

    const setTypeFilterAndSync = (nextTypeFilter: TypeFilter) => {
        setTypeFilter(nextTypeFilter);
        updateUrlParams(
            {
                [TYPE_PARAM_KEY]: nextTypeFilter,
            },
            hasGroups
        );
    };

    const channels = useMemo(() => {
        if (loading) return [];

        const filteredChannels = getFilteredChannelsByGroups({
            channels: allChannels,
            hasGroups,
            channelGroupsWithChannels,
            selectedGroupId,
            filterType,
        });
        const typeFilteredChannels = getFilteredChannelsByType(
            filteredChannels,
            typeFilter
        );

        return getSortedChannels(
            typeFilteredChannels,
            sortType,
            selectedGroupId
        );
    }, [
        allChannels,
        channelGroupsWithChannels,
        filterType,
        hasGroups,
        loading,
        selectedGroupId,
        sortType,
        typeFilter,
    ]);

    const resetSortTypeToDefault = () => {
        setSortTypeAndSync(SortType.None);
        setTypeFilterAndSync(TypeFilter.All);
    };

    const value: FilterContextType = {
        filterType,
        selectedGroupId,
        sortType,
        typeFilter,
        hasGroups,
        channelGroupsWithChannels,
        channels,
        loading,
        setFilterType: setFilterTypeAndSync,
        setSelectedGroupId: setSelectedGroupIdAndSync,
        setSortType: setSortTypeAndSync,
        setTypeFilter: setTypeFilterAndSync,
        resetSortTypeToDefault,
    };

    return (
        <FilterContext.Provider value={value}>
            {children}
        </FilterContext.Provider>
    );
};

export const useFilter = (): FilterContextType => {
    const context = useContext(FilterContext);
    if (context === undefined) {
        throw new Error('useFilter must be used within a FilterProvider');
    }
    return context;
};
